import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { fetchTwentyLoginToken } from "./fetchTwentySso";
import type { TwentyCrmEmbedProps, TwentyCrmEmbedRef } from "./types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { Users, AlertCircle, RefreshCw } from "lucide-react";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

const TWENTY_CRM_FALLBACK_URL = "https://crm.zaviago.com";
const LOAD_TIMEOUT_MS = 30000;

/**
 * Session storage keys to track auth + workspace URL this session.
 * Scoped per user email to prevent cross-user leaks.
 */
const TWENTY_AUTH_SESSION_PREFIX = "twenty_authenticated_";
const TWENTY_CRM_URL_PREFIX = "twenty_crm_url_";

function getTwentySessionCrmUrl(userEmail: string): string | null {
  try {
    return sessionStorage.getItem(TWENTY_CRM_URL_PREFIX + userEmail);
  } catch {
    return null;
  }
}

function isTwentyAuthenticatedThisSession(userEmail: string): boolean {
  try {
    return sessionStorage.getItem(TWENTY_AUTH_SESSION_PREFIX + userEmail) === "true";
  } catch {
    return false;
  }
}

function markTwentyAuthenticated(userEmail: string, crmUrl: string): void {
  try {
    sessionStorage.setItem(TWENTY_AUTH_SESSION_PREFIX + userEmail, "true");
    sessionStorage.setItem(TWENTY_CRM_URL_PREFIX + userEmail, crmUrl);
  } catch {
    // Ignore
  }
}

export function clearTwentySession(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(TWENTY_AUTH_SESSION_PREFIX) || key?.startsWith(TWENTY_CRM_URL_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore
  }
}

export const TwentyCrmEmbed = forwardRef<TwentyCrmEmbedRef, TwentyCrmEmbedProps>(
  function TwentyCrmEmbed(props, ref) {
    const { className, onReady, onError } = props;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [iframeReady, setIframeReady] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [iframeKey, setIframeKey] = useState(0);

    const currentTenant = useAuthStore((state) => state.currentTenant);
    const username = useAuthStore((state) => state.username);
    const isProvisioned = useAuthStore((state) => state.isProvisioned);

    // Load timeout
    useEffect(() => {
      if (iframeReady) return;
      if (!iframeSrc) return;

      const timeout = setTimeout(() => {
        if (!iframeReady) {
          setLoadError("CRM took too long to load. Please try again.");
          onError?.("CRM load timeout");
        }
      }, LOAD_TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }, [iframeReady, iframeSrc, onError]);

    // SSO flow: get loginToken → load iframe with /verify?loginToken=XXX
    useEffect(() => {
      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      // If already authenticated this session, load CRM directly on workspace subdomain
      if (isTwentyAuthenticatedThisSession(username)) {
        const cachedUrl = getTwentySessionCrmUrl(username) || TWENTY_CRM_FALLBACK_URL;
        setIframeSrc(cachedUrl);
        setIsLoading(false);
        return;
      }

      let cancelled = false;
      setIsLoading(true);
      setLoadError(null);

      fetchTwentyLoginToken(currentTenant, username)
        .then(({ loginToken, crmUrl }) => {
          if (cancelled) return;
          // Load Twenty with loginToken on workspace subdomain — VerifyLoginTokenEffect handles exchange
          setIframeSrc(`${crmUrl}/verify?loginToken=${encodeURIComponent(loginToken)}`);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("[TwentyCrmEmbed] SSO error:", err);
          setLoadError(
            err instanceof Error ? err.message : "Failed to authenticate with CRM"
          );
          setIsLoading(false);
          onError?.(err instanceof Error ? err.message : "SSO error");
        });

      return () => { cancelled = true; };
    }, [currentTenant, username, onError]);

    const handleIframeLoad = useCallback(() => {
      setIframeReady(true);
      // Extract the origin from current iframe src to persist for session restore
      const origin = iframeSrc ? new URL(iframeSrc).origin : TWENTY_CRM_FALLBACK_URL;
      markTwentyAuthenticated(username ?? "", origin);
      onReady?.();
    }, [onReady, username, iframeSrc]);

    const handleIframeError = useCallback(() => {
      setLoadError("Failed to load CRM. Please check your connection.");
      onError?.("iframe load error");
    }, [onError]);

    // Force reload with fresh SSO
    const reload = useCallback(() => {
      setLoadError(null);
      setIframeReady(false);
      setIframeSrc(null);
      setIsLoading(true);

      if (!currentTenant || !username) {
        setLoadError("Missing tenant or user information");
        setIsLoading(false);
        return;
      }

      // Clear session cache to force fresh SSO
      try {
        sessionStorage.removeItem(TWENTY_AUTH_SESSION_PREFIX + username);
      } catch {
        // Ignore
      }

      fetchTwentyLoginToken(currentTenant, username)
        .then(({ loginToken, crmUrl }) => {
          setIframeSrc(`${crmUrl}/verify?loginToken=${encodeURIComponent(loginToken)}`);
          setIsLoading(false);
          setIframeKey((prev) => prev + 1);
        })
        .catch((err) => {
          console.error("[TwentyCrmEmbed] SSO reload error:", err);
          setLoadError(
            err instanceof Error ? err.message : "Failed to get SSO URL"
          );
          setIsLoading(false);
          onError?.(err instanceof Error ? err.message : "SSO error");
        });
    }, [currentTenant, username, onError]);

    useImperativeHandle(
      ref,
      () => ({ reload, isReady: iframeReady }),
      [reload, iframeReady]
    );

    // Not provisioned state
    if (!isProvisioned) {
      return (
        <div className={cn("flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50", className)}>
          <Users className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">CRM Not Available</h2>
          <p className="text-gray-500 max-w-md">
            CRM has not been set up for your workspace. Please contact your administrator.
          </p>
        </div>
      );
    }

    // Loading state
    if (isLoading) {
      return (
        <div className={cn("flex flex-col items-center justify-center h-full bg-gray-50", className)}>
          <AnimatedStarburst size={48} className="mb-4" />
          <p className="text-gray-500">Connecting to CRM...</p>
        </div>
      );
    }

    // Error state
    if (loadError) {
      return (
        <div className={cn("flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50", className)}>
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Unable to load CRM</h2>
          <p className="text-gray-500 max-w-md mb-4">{loadError}</p>
          <button
            onClick={reload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    // No URL yet
    if (!iframeSrc) {
      return null;
    }

    // Render iframe
    return (
      <div className={cn("twenty-crm-embed-wrapper relative w-full h-full min-h-[400px] bg-gray-50", className)}>
        {!iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="flex flex-col items-center">
              <AnimatedStarburst size={48} className="mb-4" />
              <p className="text-gray-500">Loading CRM...</p>
            </div>
          </div>
        )}
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={iframeSrc}
          className="absolute inset-0 w-full h-full border-none"
          title="Twenty CRM"
          allow="clipboard-read; clipboard-write"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
    );
  }
);

TwentyCrmEmbed.displayName = "TwentyCrmEmbed";
